import { useEffect, useState } from "react";
import { Star, Loader2, Plus, Trash2, Eye, EyeOff, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/**
 * PublishedReviewsCard — „Bewertungen veröffentlicht".
 * Der Vermittler/Verkäufer sammelt Erfahrungsberichte (Social Proof) und
 * kann sie veröffentlichen/verbergen — als Marketing-Material für seine
 * Empfehlungs-Seite.
 */
export default function PublishedReviewsCard() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ author: "", city: "", rating: 5, text: "" });

    const load = async () => {
        try {
            const { data } = await api.get("/vermittler/testimonials");
            setItems(data.items || []);
        } catch {
            /* offline */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.author.trim()) { toast.error("Bitte Name angeben"); return; }
        if (!form.text.trim()) { toast.error("Bitte Bewertungstext angeben"); return; }
        setSaving(true);
        try {
            await api.post("/vermittler/testimonials", form);
            toast.success("Bewertung gespeichert & veröffentlicht");
            setForm({ author: "", city: "", rating: 5, text: "" });
            setOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const togglePublish = async (t) => {
        try {
            await api.patch(`/vermittler/testimonials/${t.id}`, { published: !t.published });
            await load();
        } catch {
            toast.error("Aktualisierung fehlgeschlagen");
        }
    };

    const remove = async (t) => {
        if (!window.confirm("Diese Bewertung wirklich löschen?")) return;
        try {
            await api.delete(`/vermittler/testimonials/${t.id}`);
            await load();
        } catch {
            toast.error("Löschen fehlgeschlagen");
        }
    };

    const publishedCount = items.filter((i) => i.published).length;

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="published-reviews-card">
            <div className="flex items-center gap-3 p-5 border-b border-gray-100">
                <MessageSquareQuote className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-base">Bewertungen veröffentlicht</h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                        {publishedCount} von {items.length} Bewertung{items.length === 1 ? "" : "en"} veröffentlicht — Social Proof für Ihre Empfehlungen.
                    </p>
                </div>
                <Button
                    onClick={() => setOpen((o) => !o)}
                    className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-xs h-9"
                    data-testid="published-reviews-add-btn"
                >
                    <Plus className="w-4 h-4 mr-1" /> Bewertung
                </Button>
            </div>

            {open && (
                <div className="p-5 border-b border-gray-100 bg-gray-50" data-testid="published-reviews-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[11px] uppercase tracking-wider font-bold">Name *</Label>
                            <Input value={form.author} onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))} placeholder="z. B. Max M." className="rounded-sm" data-testid="review-author" />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-wider font-bold">Stadt</Label>
                            <Input value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} placeholder="z. B. München" className="rounded-sm" data-testid="review-city" />
                        </div>
                    </div>
                    <div className="mt-3">
                        <Label className="text-[11px] uppercase tracking-wider font-bold">Bewertung</Label>
                        <div className="flex items-center gap-1 mt-1" data-testid="review-rating">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button key={n} type="button" onClick={() => setForm((p) => ({ ...p, rating: n }))} data-testid={`review-star-${n}`}>
                                    <Star className={`w-6 h-6 ${n <= form.rating ? "text-[#FFCC00] fill-[#FFCC00]" : "text-gray-300"}`} />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="mt-3">
                        <Label className="text-[11px] uppercase tracking-wider font-bold">Text *</Label>
                        <Textarea value={form.text} onChange={(e) => setForm((p) => ({ ...p, text: e.target.value }))} rows={3} placeholder="z. B. Schnelle, freundliche Vermittlung — gerne wieder!" className="rounded-sm text-sm" data-testid="review-text" />
                    </div>
                    <div className="mt-3 flex justify-end">
                        <Button onClick={submit} disabled={saving} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid="review-submit">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Veröffentlichen"}
                        </Button>
                    </div>
                </div>
            )}

            <div className="p-5">
                {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                ) : items.length === 0 ? (
                    <div className="text-sm text-gray-500 text-center py-4" data-testid="published-reviews-empty">
                        Noch keine Bewertungen erfasst. Sammeln Sie Erfahrungsberichte zufriedener Kunden und veröffentlichen Sie sie als Social Proof.
                    </div>
                ) : (
                    <ul className="space-y-3" data-testid="published-reviews-list">
                        {items.map((t) => (
                            <li key={t.id} className={`border rounded-sm p-3 ${t.published ? "border-gray-200" : "border-dashed border-gray-300 opacity-70"}`} data-testid={`review-item-${t.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="flex">
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <Star key={n} className={`w-3.5 h-3.5 ${n <= t.rating ? "text-[#FFCC00] fill-[#FFCC00]" : "text-gray-300"}`} />
                                                ))}
                                            </div>
                                            <span className="text-sm font-bold text-[#111827] truncate">{t.author}</span>
                                            {t.city && <span className="text-[11px] text-gray-500">· {t.city}</span>}
                                        </div>
                                        <p className="text-sm text-gray-700 mt-1">{t.text}</p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button onClick={() => togglePublish(t)} className="p-1.5 rounded-sm hover:bg-gray-100" title={t.published ? "Verbergen" : "Veröffentlichen"} data-testid={`review-toggle-${t.id}`}>
                                            {t.published ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                                        </button>
                                        <button onClick={() => remove(t)} className="p-1.5 rounded-sm hover:bg-red-50" title="Löschen" data-testid={`review-delete-${t.id}`}>
                                            <Trash2 className="w-4 h-4 text-red-500" />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
